import { useEffect, useState } from "react";
import { Routes, Route, Link, Navigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { Bell, BellOff, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";

export const downloadInvoice = async (orderId) => {
  try {
    const res = await api.get(`/orders/${orderId}/invoice`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a");
    a.href = url; a.download = `GeneticsTrade-Invoice-${orderId}.pdf`;
    a.click();
    URL.revokeObjectURL(url);
  } catch { toast.error("Invoice download failed"); }
};

function Dashboard() {
  const [orders, setOrders] = useState([]);
  const [searches, setSearches] = useState([]);
  const { user } = useAuth();
  const { fmt } = useCurrency();
  useEffect(() => {
    api.get("/orders/mine").then(r => setOrders(r.data.orders));
    api.get("/saved-searches").then(r => setSearches(r.data.saved_searches)).catch(() => {});
  }, []);
  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
      <h1 className="heading text-3xl font-extrabold tracking-tight">My orders</h1>
      <p className="text-stone-600 mt-1">Track escrow status, health verification, and cold-chain shipments.</p>
      <div className="mt-6 surface-card overflow-hidden">
        <table className="data-table">
          <thead><tr><th>Order</th><th>Lot</th><th>Qty</th><th>Total</th><th>Status</th><th></th></tr></thead>
          <tbody>
            {orders.filter(o => !user || o.buyer_id === user.user_id).map(o => (
              <tr key={o.order_id}>
                <td className="mono text-xs">{o.order_id}</td>
                <td>{o.lot_title}<div className="text-xs text-stone-500">{o.breed} · {o.species}</div></td>
                <td className="mono">{o.quantity}</td>
                <td className="mono">{fmt(o.total)}</td>
                <td><span className="text-xs mono uppercase px-2 py-1 rounded-full pill-watch">{o.status}</span></td>
                <td className="whitespace-nowrap"><Link to={`/orders/${o.order_id}`} data-testid={`buyer-open-${o.order_id}`} className="text-[color:var(--forest-deep)] underline text-sm">Open</Link>{" · "}<button data-testid={`buyer-invoice-${o.order_id}`} onClick={() => downloadInvoice(o.order_id)} className="text-[color:var(--forest-deep)] underline text-sm">Invoice</button></td>
              </tr>
            ))}
            {orders.length === 0 && <tr><td colSpan={6} className="text-stone-500 text-center py-6">No orders yet. Visit the <Link to="/marketplace" className="underline">marketplace</Link>.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="mt-6 surface-card p-5" data-testid="saved-searches-card">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="heading font-semibold">Saved searches &amp; alerts</div>
            <div className="text-xs text-stone-500 mt-0.5">Instant email when a new lot matches — e.g. a CRITICAL heritage breed listing.</div>
          </div>
          <Link to="/marketplace" className="text-sm underline text-[color:var(--forest-deep)]" data-testid="saved-searches-new-link">Create from marketplace filters</Link>
        </div>
        {searches.length === 0 ? (
          <div className="text-sm text-stone-500 py-4">No saved searches yet.</div>
        ) : (
          <div className="mt-3 space-y-2">
            {searches.map(s => (
              <div key={s.id} data-testid={`saved-search-${s.id}`} className="flex items-center gap-3 text-sm border border-stone-200 rounded-md px-3 py-2 flex-wrap">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-medium">{s.name}</div>
                  <div className="text-xs text-stone-500 mono">{Object.entries(s.filters || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "All lots"}</div>
                </div>
                <Button type="button" variant="outline" size="sm" data-testid={`saved-search-toggle-${s.id}`} onClick={async () => { const { data } = await api.patch(`/saved-searches/${s.id}`, { alert: !s.alert }); setSearches(searches.map(x => x.id === s.id ? data : x)); }}>
                  {s.alert ? <><Bell className="h-3.5 w-3.5 mr-1" /> Alerts on</> : <><BellOff className="h-3.5 w-3.5 mr-1" /> Alerts off</>}
                </Button>
                <Button type="button" variant="outline" size="sm" data-testid={`saved-search-delete-${s.id}`} onClick={async () => { await api.delete(`/saved-searches/${s.id}`); setSearches(searches.filter(x => x.id !== s.id)); toast.success("Saved search deleted"); }}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function BuyerPortal() { return <Routes><Route index element={<Dashboard />} /><Route path="*" element={<Navigate to="/buyer" replace />} /></Routes>; }
